<template>
    <CollapsibleWidget>
        <template #name>Position</template>
        <template #controls>
            <Icon type="md-trash" @click.exact.stop="resetStyles('ElementPosition')" />
        </template>
        <div class="element-position">
            <EditableSettingsControl v-if="isTranslationMethodVisible" title="Translation Mode">
                <template #button>
                    <div class="editable-settings-item">
                        <Icon type="ios-browsers-outline" />
                    </div>
                </template>

                <template #value>
                    <Select
                        v-model="translationMethod"
                        size="small"
                        class="hox-select-dark"
                        :placeholder="getPlaceholderValue(EditableSettingProperty.TranslationMethod)"
                    >
                        <Option v-for="method in translationMethods" :key="method" :value="method">
                            {{ method }}
                        </Option>
                    </Select>
                </template>
            </EditableSettingsControl>
            <div class="keyboard-wrapper">
                <EditableSettingsControl title="Dimensions">
                    <template #button>
                        <div class="editable-settings-item">
                            <ruler-icon width="20" height="20" />
                        </div>
                    </template>
                    <template #value>
                        <div class="dimension-wrapper">
                            <div v-if="isCampaignPSD" class="dimension-wrapper__aspect-ratio">
                                <p>Preserve Aspect Ratio</p>
                                <Tooltip
                                    :content="tooltipMessage"
                                    :disabled="!tooltipMessage"
                                    placement="right"
                                    transfer
                                    max-width="550"
                                    transfer-class-name="dimensions-tooltip"
                                >
                                    <Switch
                                        v-model="preserveAspectRatio"
                                        size="small"
                                        :disabled="isCampaignPSD && isTextEditable"
                                    />
                                </Tooltip>
                            </div>

                            <div style="display: flex">
                                <div class="element-position__position">
                                    <EditableSettingsNumberInput
                                        v-model="width"
                                        :suffix="isCampaignPSD && isTextEditable ? '%' : 'px'"
                                        :show-suffix="isSuffixVisible(EditableSettingProperty.Width)"
                                        :placeholder="getPlaceholderValue(EditableSettingProperty.Width)"
                                    >
                                        <template #label>
                                            <span class="editable-settings__input-label">W:</span>
                                        </template>
                                    </EditableSettingsNumberInput>

                                    <EditableSettingsNumberInput
                                        v-model="height"
                                        :suffix="isCampaignPSD && isTextEditable ? '%' : 'px'"
                                        :show-suffix="isSuffixVisible(EditableSettingProperty.Height)"
                                        :placeholder="getPlaceholderValue(EditableSettingProperty.Height)"
                                    >
                                        <template #label>
                                            <span
                                                class="editable-settings__input-label editable-settings__input-label--second"
                                            >
                                                H:
                                            </span>
                                        </template>
                                    </EditableSettingsNumberInput>
                                    <Tooltip
                                        :content="warningMessage"
                                        :disabled="!warningMessage"
                                        placement="right"
                                        transfer
                                        max-width="550"
                                        transfer-class-name="dimensions-tooltip"
                                    >
                                        <Icon v-if="warningMessage" type="ios-warning" color="#ff9900" />
                                    </Tooltip>
                                </div>
                            </div>
                        </div>
                    </template>
                </EditableSettingsControl>
            </div>

            <div class="keyboard-wrapper">
                <EditableSettingsControl title="Position">
                    <template #button>
                        <div class="editable-settings-item">
                            <Icon type="md-move" />
                        </div>
                    </template>
                    <template #value>
                        <div class="element-position__position">
                            <EditableSettingsNumberInput
                                v-model="left"
                                :show-suffix="isSuffixVisible(EditableSettingProperty.Left)"
                                :placeholder="getPlaceholderValue(EditableSettingProperty.Left)"
                            >
                                <template #label>
                                    <span class="editable-settings__input-label">X:</span>
                                </template>
                            </EditableSettingsNumberInput>

                            <EditableSettingsNumberInput
                                v-model="top"
                                :show-suffix="isSuffixVisible(EditableSettingProperty.Top)"
                                :placeholder="getPlaceholderValue(EditableSettingProperty.Top)"
                            >
                                <template #label>
                                    <span class="editable-settings__input-label editable-settings__input-label--second">
                                        Y:
                                    </span>
                                </template>
                            </EditableSettingsNumberInput>
                        </div>
                    </template>
                </EditableSettingsControl>
            </div>

            <EditableSettingsControl v-if="!isCampaignPSD" :title="camelToUpperCase(EditableSettingProperty.Scale)">
                <template #button>
                    <div class="editable-settings-item">
                        <Icon type="md-expand" />
                    </div>
                </template>
                <template #value>
                    <EditableSettingsNumberInput
                        v-model="scale"
                        :show-suffix="true"
                        suffix="%"
                        :placeholder="getPlaceholderValue(EditableSettingProperty.Scale)"
                    />
                </template>
            </EditableSettingsControl>
        </div>
    </CollapsibleWidget>
</template>
<script lang="ts" setup>
import { computed } from "vue";
import { EditableType, LegacyEditableTypes } from "shared-utils/enums/editables";
import type { CSSSettings } from "shared-types";

import EditableSettingsControl from "@/components/Campaign/SettingsPane/EditableSettingsControl.vue";
import CollapsibleWidget from "@/components/Campaign/widgets/CollapsibleWidget.vue";

import RulerIcon from "@/assets/text-editor/ruler.svg";

import { EditableSettingProperty, EditableTranslateMethod } from "@/enums/editables";
import { camelToUpperCase } from "@/utils";
import EditableSettingsNumberInput from "@/components/Campaign/SettingsPane/EditableSettingsNumberInput.vue";

import { useEditableSettings } from "@/composables/useEditableSettings";
import { useEditor } from "@/composables/useEditor";

const emit = defineEmits<(e: "onSettingsChange", value: CSSSettings | null) => void>();

const { computedStyle, boundingClientRect } = defineProps<{
    computedStyle: CSSStyleDeclaration;
    boundingClientRect: DOMRect;
}>();

const { isCampaignPSD, editedEditable } = useEditor();

const onSettingsChange = (settings: CSSSettings | null) => {
    emit("onSettingsChange", settings);
};

const {
    top,
    left,
    width,
    height,
    scale,
    translationMethod,
    preserveAspectRatio,

    isSuffixVisible,
    getPlaceholderValue,
    resetStyles
} = useEditableSettings({
    onSettingsChange,
    boundingClientRect: boundingClientRect,
    computedStyle: computedStyle
});

const translationMethods = [
    EditableTranslateMethod.Relative,
    EditableTranslateMethod.Transform,
    EditableTranslateMethod.Margin
];

const isTranslationMethodVisible = computed(
    () => !isCampaignPSD.value && editedEditable.value.editable.type !== LegacyEditableTypes.BackgroundImage
);

const isTextEditable = computed(() => {
    return (
        editedEditable.value.editable.type === EditableType.Text ||
        editedEditable.value.editable.type === EditableType.Textarea
    );
});

const tooltipMessage = computed(() => {
    if (isCampaignPSD.value && isTextEditable.value) {
        return "Width and height are set as percentages relative to parent container or the design area. To maintain a consistent look, the aspect ratio is locked—adjusting one dimension will automatically change the other.";
    }

    return null;
});

const warningMessage = computed(() => {
    if (!isCampaignPSD.value) {
        return null;
    }

    switch (true) {
        case (width.value ?? 0) < 4 && (height.value ?? 0) < 4:
            return "Both width and height must be greater than or equal to 4.";
        case (width.value ?? 0) < 4:
            return "The width must be greater than or equal to 4.";
        case (height.value ?? 0) < 4:
            return "The height must be greater than or equal to 4.";
        default:
            return null;
    }
});
</script>
<style lang="scss">
@use "@style/variables" as *;
.element-position {
    &__position {
        flex: 1 1 138px;
        width: 138px;
        display: flex;
        justify-content: flex-start;
        align-items: center;
    }
}

.dimension-wrapper {
    display: flex;
    flex-direction: column;
    flex: 1;

    .dimension-wrapper__aspect-ratio {
        display: flex;
        flex-direction: row;
        flex: 1;
        align-items: center;
        justify-content: space-between;

        p {
            line-height: normal;
        }
    }
}

.dimensions-tooltip {
    .ivu-tooltip-inner {
        white-space: normal;
        word-break: break-word;
        overflow-wrap: break-word;
    }
}
</style>
